/* eslint-disable no-nested-ternary */
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Modifier,
  ModifierCategory,
  ModifierDeactivateRequest,
  OtherModifierListRequest,
  VariantModifierPriceUpdateRequest,
} from '@/types/menus.types';
import { useSTKContext } from '@/context/context';
import { StoreGroupType } from '@/context/context.types';
import { isModifierCategoryWithSlotFilters } from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';

function coerceOptionalBool(value: unknown): boolean | undefined {
  if (value === undefined || value === null) return undefined;
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
}

function coerceRequiredBool(value: unknown): boolean {
  if (value === true || value === 'true') return true;
  return false;
}

/** Normalize `/modifiers` list payload so lock/mandatory/zero-priced flags work when API sends strings. */
export function normalizeModifierFromApi(raw: Modifier): Modifier {
  return {
    ...raw,
    priceIsLocked: coerceRequiredBool(raw.priceIsLocked),
    isMandatory: coerceOptionalBool(raw.isMandatory),
    isZeroPriced: coerceOptionalBool(raw.isZeroPriced),
    isVisible: coerceOptionalBool(raw.isVisible),
  };
}

export const useModifierCategories = (enabled: boolean) => {
  const { apiClient } = useSTKContext();

  return useQuery<ModifierCategory[]>({
    queryKey: ['modifier-categories'],
    queryFn: async () => {
      const response = await apiClient.get<ModifierCategory[]>('/modifiers/categories');
      const data = response.data;
      return Array.isArray(data) ? data : [];
    },
    enabled,
  });
};

export const useMenuModifiersGroup = (
  enabled: boolean,
  stores: string[],
  storeGroups: StoreGroupType[],
  categoryName: string,
  /** Slot-filter categories: slot + modifier ids (same PUT /modifiers endpoint, extended body). */
  slotFetchParams: { slotId: string; modifierIds: string[] } | null = null,
) => {
  const { apiClient } = useSTKContext();

  const storeGroupIds = storeGroups.map((st) => st.storeGroupId).filter((id): id is string => Boolean(id));
  const usesSlotModifierFetch = isModifierCategoryWithSlotFilters(categoryName);
  const slotBody: OtherModifierListRequest | null =
    usesSlotModifierFetch && slotFetchParams?.slotId && slotFetchParams.modifierIds.length > 0
      ? {
          modifierCategory: categoryName,
          stores,
          storeGroups: storeGroupIds,
          slotId: slotFetchParams.slotId,
          modifierIds: slotFetchParams.modifierIds,
        }
      : null;

  const standardBody = { stores, storeGroups: storeGroupIds, modifierCategory: categoryName };
  const requestBody = slotBody ?? standardBody;

  return useQuery<Modifier[]>({
    queryKey: [
      'store-grouped-modifiers',
      { ...stores, categoryName, slotFetch: usesSlotModifierFetch ? slotFetchParams : undefined },
    ],
    queryFn: async () => {
      const response = await apiClient.put<Modifier[]>(`/modifiers`, requestBody);
      const list = Array.isArray(response.data) ? response.data : [];
      return list.map(normalizeModifierFromApi);
    },
    enabled,
  });
};

export const useUpdateProductPrice = () => {
  const { apiClient } = useSTKContext();
  return useMutation(async (requestBody: VariantModifierPriceUpdateRequest[]) => {
    const response = await apiClient.put('/modifiers/update-variant-price', requestBody);
    return response.data;
  });
};

export const useDeactivateModifiers = () => {
  const { apiClient } = useSTKContext();
  return useMutation(async (requestBody: ModifierDeactivateRequest) => {
    const response = await apiClient.put('/modifiers/deactivate', requestBody);
    return response.data;
  });
};

export const useModifierPriceDeviations = (
  enabled: boolean,
  modifierCategory: string,
  stores: string[],
  modifierKeys: {
    modifierId: string;
    slotId: string;
    modifierWeightId: string;
    variantId: string;
  }[],
) => {
  const { apiClient } = useSTKContext();
  const reqBody = {
    modifierKeys: Array.isArray(modifierKeys)
      ? typeof modifierKeys[0] === 'string'
        ? [
            {
              modifierId: modifierKeys[0] || '',
              slotId: modifierKeys[1] || '',
              modifierWeightId: modifierKeys[2] || '',
              variantId: modifierKeys[3] || '',
            },
          ]
        : modifierKeys.map((key) => ({
            modifierId: key.modifierId || '',
            slotId: key.slotId || '',
            modifierWeightId: key.modifierWeightId || '',
            variantId: key.variantId || '',
          }))
      : [],
    stores,
    modifierCategory,
  };
  return useQuery({
    queryKey: ['modifierPriceDeviations', reqBody],
    queryFn: async () => {
      const { data } = await apiClient.put(`/modifiers/price-deviations`, reqBody);
      return data;
    },
    enabled,
  });
};
